import type { Tables } from '@biopeak/db';
import type { Session } from '@supabase/supabase-js';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { supabase } from './supabase.ts';

export type Doctor = Tables<'doctors'>;

/**
 * Estado de sessão do médico.
 *
 * Diferente do app do paciente, aqui NÃO existe onboarding que crie a linha
 * em `doctors` — o cadastro é administrativo, por script SQL (build-prompt
 * §9). Um usuário autenticado sem linha em `doctors` não tem "próximo passo":
 * é acesso negado, ponto final. O papel continua sendo derivado da EXISTÊNCIA
 * da linha, nunca de uma coluna — o mesmo princípio do app do paciente.
 */

interface SessionState {
  loading: boolean;
  session: Session | null;
  doctor: Doctor | null;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [doctor, setDoctor] = useState<Doctor | null>(null);

  const loadDoctor = useCallback(async (current: Session | null) => {
    if (!current) {
      setDoctor(null);
      return;
    }

    const { data, error } = await supabase
      .from('doctors')
      .select('*')
      .eq('user_id', current.user.id)
      .maybeSingle();

    if (error) {
      console.error('Falha ao carregar medico', error);
      setDoctor(null);
      return;
    }
    setDoctor(data);
  }, []);

  useEffect(() => {
    let active = true;

    supabase.auth.getSession().then(async ({ data }) => {
      if (!active) return;
      setSession(data.session);
      await loadDoctor(data.session);
      if (active) setLoading(false);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange(
      async (_event, next) => {
        if (!active) return;
        setSession(next);
        await loadDoctor(next);
      },
    );

    return () => {
      active = false;
      subscription.subscription.unsubscribe();
    };
  }, [loadDoctor]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setDoctor(null);
  }, []);

  const value = useMemo(
    () => ({ loading, session, doctor, signOut }),
    [loading, session, doctor, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession precisa estar dentro de <SessionProvider>');
  }
  return context;
}
